import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Plus, Trash2 } from 'lucide-react';

import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SaveBar } from '@/components/ui/save-bar';
import { useBrief, useUpdateBriefSlice } from '@/lib/brief';

export const Route = createFileRoute('/_app/brief/accreditations')({
  staticData: { crumb: 'Akredytacje' },
  component: AccreditationsPage,
});

interface Accreditation {
  name: string;
  body?: string;
  year_obtained?: string;
  valid_until?: string;
  url?: string;
}
interface Membership {
  name: string;
  organization?: string;
  since?: string;
}
interface Award {
  title: string;
  awarder?: string;
  year?: string;
  url?: string;
}

function AccreditationsPage() {
  const { data: brief } = useBrief();
  const update = useUpdateBriefSlice();
  const [accs, setAccs] = React.useState<Accreditation[]>(
    () => (brief?.accreditations ?? []) as Accreditation[]
  );
  const [memberships, setMemberships] = React.useState<Membership[]>(
    () => (brief?.memberships ?? []) as Membership[]
  );
  const [awards, setAwards] = React.useState<Award[]>(
    () => (brief?.awards ?? []) as Award[]
  );
  const [dirty, setDirty] = React.useState(false);
  const [savedStatus, setSavedStatus] = React.useState<'idle' | 'saved' | 'error'>('idle');

  React.useEffect(() => {
    if (!dirty) {
      setAccs((brief?.accreditations ?? []) as Accreditation[]);
      setMemberships((brief?.memberships ?? []) as Membership[]);
      setAwards((brief?.awards ?? []) as Award[]);
    }
  }, [brief?.accreditations, brief?.memberships, brief?.awards, dirty]);

  const onSave = async () => {
    setSavedStatus('idle');
    try {
      await update.mutateAsync({
        patch: {
          accreditations: accs.filter((a) => a.name?.trim().length >= 3),
          memberships: memberships.filter((m) => m.name?.trim().length >= 3),
          awards: awards.filter((a) => a.title?.trim().length >= 3),
        },
      });
      setSavedStatus('saved');
      setDirty(false);
    } catch {
      setSavedStatus('error');
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
      className="space-y-8"
    >
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
          Akredytacje, członkostwa, wyróżnienia
        </h1>
        <p className="mt-1 text-sm text-text-muted">
          Dane organizacji, do których należycie i certyfikatów które posiadacie. Pokazujemy
          w sekcji „Standardy i przynależności" oraz w sekcji logos.
        </p>
      </header>

      <ListEditor
        title="Akredytacje"
        items={accs}
        setItems={(next) => {
          setAccs(next);
          setDirty(true);
        }}
        empty={{ name: '' }}
        columns={[
          { key: 'name', label: 'Nazwa', placeholder: 'AAHA Hospital Accreditation' },
          { key: 'body', label: 'Wydający', placeholder: 'American Animal Hospital Association' },
          { key: 'year_obtained', label: 'Rok', placeholder: '2020' },
          { key: 'url', label: 'Link', placeholder: 'https://...' },
        ]}
      />

      <ListEditor
        title="Członkostwa towarzystw"
        items={memberships}
        setItems={(next) => {
          setMemberships(next);
          setDirty(true);
        }}
        empty={{ name: '' }}
        columns={[
          { key: 'name', label: 'Nazwa', placeholder: 'European Veterinary Dental Society' },
          { key: 'organization', label: 'Organizacja', placeholder: 'EVDS' },
          { key: 'since', label: 'Od', placeholder: '2015' },
        ]}
      />

      <ListEditor
        title="Wyróżnienia i nagrody"
        items={awards}
        setItems={(next) => {
          setAwards(next);
          setDirty(true);
        }}
        empty={{ title: '' }}
        columns={[
          { key: 'title', label: 'Tytuł', placeholder: 'Najlepsza klinika roku 2023' },
          { key: 'awarder', label: 'Przyznane przez', placeholder: 'Forbes Polska' },
          { key: 'year', label: 'Rok', placeholder: '2023' },
          { key: 'url', label: 'Link', placeholder: 'https://...' },
        ]}
      />

      <SaveBar
        saving={update.isPending}
        dirty={dirty}
        status={savedStatus}
        onCancel={() => {
          setAccs((brief?.accreditations ?? []) as Accreditation[]);
          setMemberships((brief?.memberships ?? []) as Membership[]);
          setAwards((brief?.awards ?? []) as Award[]);
          setDirty(false);
        }}
      />
    </form>
  );
}

interface Column {
  key: string;
  label: string;
  placeholder?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRow = Record<string, any>;

function ListEditor<T extends AnyRow>({
  title,
  items,
  setItems,
  empty,
  columns,
}: {
  title: string;
  items: T[];
  setItems: (next: T[]) => void;
  empty: Partial<T>;
  columns: Column[];
}) {
  const updateField = (idx: number, key: string, value: string) => {
    setItems(items.map((it, i) => (i === idx ? ({ ...it, [key]: value } as T) : it)));
  };
  return (
    <section className="space-y-3 rounded-[var(--radius-card)] bg-bg p-6 ring-1 ring-black/5">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-base font-semibold text-text">
          {title}
          <span className="ml-2 text-sm font-normal text-text-subtle tabular-nums">
            {items.length}
          </span>
        </h2>
        <Button
          type="button"
          variant="secondary"
          onClick={() => setItems([...items, { ...empty } as T])}
        >
          <Plus className="size-4" aria-hidden="true" /> Dodaj
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-text-subtle">Brak wpisów. Sekcja zostanie ukryta.</p>
      ) : (
        <ul role="list" className="space-y-3">
          {items.map((it, idx) => (
            <li key={idx} className="grid gap-3 md:grid-cols-[repeat(4,minmax(0,1fr))_3rem]">
              {columns.map((c) => (
                <Field key={c.key} label={c.label} htmlFor={`${title}-${c.key}-${idx}`}>
                  <Input
                    id={`${title}-${c.key}-${idx}`}
                    value={((it as AnyRow)[c.key] as string | undefined) ?? ''}
                    onChange={(e) => updateField(idx, c.key, e.target.value)}
                    placeholder={c.placeholder}
                  />
                </Field>
              ))}
              {/* Filler when fewer than 4 columns: keeps the grid stable. */}
              {Array.from({ length: Math.max(0, 4 - columns.length) }).map((_, i) => (
                <div key={`gap-${i}`} />
              ))}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="self-end mb-0.5"
                aria-label="Usuń wpis"
                onClick={() => setItems(items.filter((_, i) => i !== idx))}
              >
                <Trash2 className="size-4" aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
